import {
  IError,
  LoadingOverlay,
  Spinner,
  formatDate,
  getGivenNameFromProfile,
  useToast,
  Vendors,
} from '@comptia-sso/core';
import ArrowForwardIosIconSharp from '@material-ui/icons/ArrowForwardIos';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';

import classNames from 'classnames';
import { ReactElement, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

// Components.

// Hooks.
import {
  useProfile,
  useProfileEntitlements,
  useRedirectToVendor,
  useTrackEvent,
  useTrackOdpEvent,
} from 'hooks';

// Layouts.
import { ContentLayout } from 'layouts';

// Styles.
import styles from './TrainingProducts.module.scss';

export const TrainingProductsView = (): ReactElement => {
  const { t } = useTranslation();
  const [popToast] = useToast();
  const [profile, { isLoading: isLoadingProfile }] = useProfile();
  const [
    entitlements,
    { error: entitlementsError, isLoading: isLoadingEntitlements },
  ] = useProfileEntitlements(profile?.id);
  const [redirectToVendor, { isLoading: isRedirecting }] = useRedirectToVendor(
    profile?.id,
  );
  const trackEvent = useTrackEvent('My Training Products');
  const trackOdpEvent = useTrackOdpEvent();

  useEffect(() => {
    if (entitlementsError) {
      popToast(
        (entitlementsError as IError)?.message || t('Toast.Error.Default'),
      );
    }
  }, [entitlementsError]);

  const renderUserEntitlements = () => {
    const activeEntitlements = renderEntitlements('active');
    const expiredEntitlements = renderEntitlements('expired');

    return activeEntitlements.concat(expiredEntitlements);
  };

  const renderEntitlements = (group: string) =>
    entitlements
      ?.sort(function (a, b) {
        return group === 'active'
          ? new Date(b.redemptionDate).valueOf() -
              new Date(a.redemptionDate).valueOf()
          : new Date(b.expirationDate).valueOf() -
              new Date(a.expirationDate).valueOf();
      })
      .map((entitlement, index) => {
        const expirationDate = new Date(entitlement.expirationDate);
        const isActive =
          expirationDate >= new Date() ||
          (!entitlement.expirationDate &&
            !entitlement.redemptionDate &&
            entitlement.vendor === Vendors.BenchPrep);

        return (
          ((group === 'active' && isActive) ||
            (group === 'expired' && !isActive)) && (
            <a
              className={`${styles.product} ${!isActive ? styles.expired : ''}`}
              href="#"
              key={entitlement.id}
              onClick={async (e) => {
                e.preventDefault();
                trackEvent('Clicked', { label: entitlement.product?.title });
                trackOdpEvent(
                  'navigation',
                  'Launchpad Training - My Training',
                  entitlement.product?.title,
                );
                if (isActive) {
                  try {
                    await redirectToVendor(entitlement.vendor, {
                      classroom: entitlement.product?.classroom,
                      productCode: entitlement.product?.vendorProductCode,
                      version: entitlement.preferredProductVersion,
                    });
                  } catch (error) {
                    popToast(
                      (error as IError)?.message || t('Toast.Error.Default'),
                    );
                  }
                }
              }}
            >
              <div className={styles.productCourseName}>
                {entitlement.product?.title}
              </div>
              <div
                className={classNames(styles.productExpirationDate, {
                  [styles.expired]: !isActive,
                })}
              >
                <span>
                  {!isActive
                    ? t('TrainingProducts.ExpiredOn')
                    : t('TrainingProducts.ExpiresOn')}
                </span>
                {formatDate(entitlement.expirationDate)}
              </div>
              {entitlement.migratedToProductTitle !== '' &&
                entitlement.migratedToProductTitle !== null && (
                  <div className={styles.migrated}>
                    <span>
                      <ReportProblemOutlinedIcon />
                    </span>
                    <span className={styles.toolTip}>
                      {t('AccessYourTraining.MigrateContent1')}{' '}
                      {entitlement.migratedToProductTitle}.{' '}
                      {t('AccessYourTraining.MigrateContent2')}{' '}
                      {entitlement.product.title}{' '}
                      {t('AccessYourTraining.MigrateContent3')}{' '}
                      {entitlement.migratedToProductTitle}.
                    </span>
                  </div>
                )}
              <div className={styles.productCarret}>
                <ArrowForwardIosIconSharp />
              </div>
            </a>
          )
        );
      })
      .filter(Boolean);

  return (
    <>
      <Helmet>
        <title>My Training Courseware</title>
      </Helmet>
      <ContentLayout
        subtitle={t('TrainingProducts.Subheading')}
        title={getGivenNameFromProfile(profile)}
      >
        <p
          dangerouslySetInnerHTML={{
            __html: t('AccessYourTraining.Content2'),
          }}
        />
        {!isLoadingProfile && (
          <div className={styles.trainingProducts}>
            <hr />
            {isLoadingEntitlements ? (
              <Spinner className={styles.spinner} />
            ) : (
              <div className={styles.products}>
                {entitlements.length > 0 ? (
                  renderUserEntitlements()
                ) : (
                  <p>{t('TrainingProducts.NoProducts')}</p>
                )}
              </div>
            )}
          </div>
        )}
      </ContentLayout>
      <LoadingOverlay isOpen={isRedirecting} text={t('Loading.Default')} />
    </>
  );
};
